import React, { useEffect, useState } from 'react';
import {
  Card,
  Checkbox,
  Dimmer,
  Grid,
  Icon,
  Label,
  Loader,
  Segment,
  Table,
  Divider,
  Message,
} from 'semantic-ui-react';
import styled from 'styled-components';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { get } from '../../../../services/request';
import moment from 'moment-timezone';
import {
  translateBatchStatus,
  getBatchStatusColor,
  BatchDashboardItem,
} from '../types';
import Button from '../../../../components/Button';
import { dangerNotification } from '../../../../services/notification';
import AuthorizationContainer from '../../../../components/AuthorizationContainer';
import { ActionEnum } from '../../../../enums/authz-action.enum';
import { FeatureEnum } from '../../../../enums/authz-feature.enum';
import EditBatchCodesModal from '../components/EditBatchCodesModal';
import TransmitBatchConfirmModal from '../components/TransmitBatchConfirmModal';
import TransferPoliciesModal from '../components/TransferPoliciesModal';
import SyncBatchNumberingModal from '../components/SyncBatchNumberingModal';
import RestartBatchModal from '../components/RestartBatchModal';
import { canTransmitBatch } from '../utils/batch-transmit.util';
import { canSyncBatchNumbering } from '../utils/batch-sync-numbering.util';
import { canRestartBatch } from '../utils/batch-restart.util';
import {
  canOpenTransferModal,
  canSelectPolicyForTransfer,
  canTransferBatch,
} from '../utils/batch-transfer.util';
import { UpdatedBatchCodesResponse, downloadYelumSpreadsheet } from '../../../../services/fire-insurance-batches';

const PageContainer = styled.div`
  padding: 20px;
`;

const InfoCard = styled(Card)`
  &.ui.card {
    width: 100%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #555;
`;

const InfoValue = styled.span`
  color: #333;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;

  h1 {
    margin: 0;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AlertBox = styled(Message)`
  &.ui.message {
    margin: 20px 0;
  }
`;

const FireInsuranceBatchDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [batch, setBatch] = useState<BatchDashboardItem | null>(null);
  const [policies, setPolicies] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [transmitModalOpen, setTransmitModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [restartModalOpen, setRestartModalOpen] = useState(false);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);
  const history = useHistory();
  const match = useRouteMatch();

  useEffect(() => {
    if (id) {
      loadBatchDetails();
    }
  }, [id]);

  const loadBatchDetails = async () => {
    setIsLoading(true);
    try {
      const batchResponse = await get(`/fire-insurance-batches/${id}`);

      // Buscar apólices do lote
      let policiesResponse: any[] = [];
      try {
        policiesResponse = await get(`/fire-insurance-batches/${id}/policies`) || [];
      } catch (e) {
        console.log('Apólices não encontradas para este lote');
        policiesResponse = [];
      }
      setPolicies(policiesResponse);

      // Calcular campos adicionais que não vêm do endpoint individual
      const activePoliciesCount = policiesResponse.filter(
        (p: any) => p.status !== 'PolicyExpired' && p.status !== 'Canceled'
      ).length;

      const today = moment();
      const endDate = moment(batchResponse.endDate);
      const daysRemaining = Math.max(0, endDate.diff(today, 'days'));

      // Enriquecer o batch com campos calculados
      const enrichedBatch: BatchDashboardItem = {
        ...batchResponse,
        itemCount: activePoliciesCount,
        hasSingleItem: activePoliciesCount === 1,
        daysRemaining,
        dueDate: null,
        transmissionDate: batchResponse.endDate,
        isNearDeadline: daysRemaining <= 5,
        isManualProcessing: batchResponse.isManualProcessing ?? false,
        updatedAt: batchResponse.updatedAt || batchResponse.createdAt,
      };

      setBatch(enrichedBatch);
    } catch (err) {
      console.error('Erro ao carregar detalhes do lote:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar detalhes do lote';
      setError(errorMessage);
      dangerNotification('Erro', 'Erro ao carregar detalhes do lote');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    // Extrair o base path removendo o /:id
    const basePath = match.url.replace(/\/[^/]+$/, '');
    history.push(basePath);
  };

  const handleEditSuccess = (updated: UpdatedBatchCodesResponse) => {
    setBatch((prev) =>
      prev
        ? {
            ...prev,
            quotationCode: updated.quotationCode,
            proposalCode: updated.proposalCode,
            policyNumber: updated.policyNumber,
            status: updated.status,
          }
        : prev,
    );
  };

  const handleDownload = async () => {
    if (!batch?.id) {
      return;
    }

    setDownloading(true);
    try {
      await downloadYelumSpreadsheet(batch.id);
    } catch (err) {
      dangerNotification(
        'Erro',
        (err as Error)?.message || 'Erro ao baixar planilha do lote',
      );
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Loader active inline="centered" size="large">
          Carregando detalhes do lote...
        </Loader>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Message negative>
          <Message.Header>Erro ao carregar lote</Message.Header>
          <p>{error}</p>
          <Button onClick={handleGoBack}>Voltar</Button>
        </Message>
      </PageContainer>
    );
  }

  if (!batch) {
    return (
      <PageContainer>
        <Message negative>
          <Message.Header>Lote não encontrado</Message.Header>
          <p>O lote solicitado não foi encontrado ou você não tem permissão para visualizá-lo.</p>
          <Button onClick={handleGoBack}>Voltar</Button>
        </Message>
      </PageContainer>
    );
  }

  const activePolicies = policies.filter((p) =>
    canSelectPolicyForTransfer(p.status),
  );

  const batchTransferEligibility = canTransferBatch({
    status: batch.status,
    insurerCode: batch.insurer?.code,
  });

  const transferEligibility = canOpenTransferModal(
    selectedPolicyIds.length,
    batchTransferEligibility,
  );

  const selectablePolicyIds = activePolicies.map((policy) => policy.id);
  const allSelected =
    selectablePolicyIds.length > 0 &&
    selectablePolicyIds.every((policyId) => selectedPolicyIds.includes(policyId));

  const handleTogglePolicy = (policyId: string, checked: boolean) => {
    setSelectedPolicyIds((current) => {
      if (checked) {
        return current.includes(policyId) ? current : [...current, policyId];
      }
      return current.filter((id) => id !== policyId);
    });
  };

  const handleToggleAllPolicies = (checked: boolean) => {
    setSelectedPolicyIds(checked ? selectablePolicyIds : []);
  };

  const selectedPoliciesForTransfer = activePolicies
    .filter((policy) => selectedPolicyIds.includes(policy.id))
    .map((policy) => ({
      id: policy.id,
      tenantName:
        policy.fireInsuranceAnalysis?.fireInsurance?.tenantName || '-',
      tenantDocument:
        policy.fireInsuranceAnalysis?.fireInsurance?.tenantDocument || '-',
    }));

  const transmitEligibility = canTransmitBatch({
    status: batch.status,
    isManualProcessing: batch.isManualProcessing,
    itemCount: activePolicies.length,
  });

  const syncNumberingEligibility = canSyncBatchNumbering({
    status: batch.status,
    itemCount: activePolicies.length,
    quotationCode: batch.quotationCode,
    insurerCode: batch.insurer?.code,
  });

  const restartEligibility = canRestartBatch({
    status: batch.status,
    isManualProcessing: batch.isManualProcessing,
    itemCount: activePolicies.length,
  });

  return (
    <PageContainer>
      {downloading ? (
        <Dimmer active inverted page>
          <Loader indeterminate>Baixando planilha do lote...</Loader>
        </Dimmer>
      ) : null}
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <Button basic onClick={handleGoBack}>
              <Icon name="arrow left" />
              Voltar
            </Button>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column>
            <HeaderRow>
              <h1>
                <Icon name="clipboard list" />
                Detalhes do Lote #{batch.batchNumber}
              </h1>
              <HeaderActions>
                <AuthorizationContainer
                  action={ActionEnum.save}
                  feature={FeatureEnum.fireinsurances}
                >
                  <Button
                    color="blue"
                    disabled={!transferEligibility.allowed}
                    title={transferEligibility.reason || 'Transferir apólices'}
                    onClick={() => setTransferModalOpen(true)}
                  >
                    <Icon name="exchange" />
                    Transferir apólices
                  </Button>
                </AuthorizationContainer>
                <AuthorizationContainer
                  action={ActionEnum.save}
                  feature={FeatureEnum.fireinsurances}
                >
                  <Button
                    color="purple"
                    disabled={!transmitEligibility.allowed}
                    title={transmitEligibility.reason || 'Transmitir lote'}
                    onClick={() => setTransmitModalOpen(true)}
                  >
                    <Icon name="paper plane" />
                    Transmitir lote
                  </Button>
                </AuthorizationContainer>
                <AuthorizationContainer
                  action={ActionEnum.save}
                  feature={FeatureEnum.fireinsurances}
                >
                  <Button
                    color="teal"
                    disabled={!syncNumberingEligibility.allowed}
                    title={syncNumberingEligibility.reason || 'Sincronizar numeração'}
                    onClick={() => setSyncModalOpen(true)}
                  >
                    <Icon name="sync" />
                    Sincronizar numeração
                  </Button>
                </AuthorizationContainer>
                <AuthorizationContainer
                  action={ActionEnum.save}
                  feature={FeatureEnum.fireinsurances}
                >
                  <Button
                    color="red"
                    disabled={!restartEligibility.allowed}
                    title={restartEligibility.reason || 'Reiniciar lote'}
                    onClick={() => setRestartModalOpen(true)}
                  >
                    <Icon name="refresh" />
                    Reiniciar lote
                  </Button>
                </AuthorizationContainer>
                <AuthorizationContainer
                  action={ActionEnum.download}
                  feature={FeatureEnum.fireinsurances}
                >
                  <Button
                    color="teal"
                    loading={downloading}
                    disabled={downloading || activePolicies.length === 0}
                    title={
                      activePolicies.length === 0
                        ? 'Lote sem itens para exportar'
                        : 'Baixar planilha do lote (formato Yelum)'
                    }
                    onClick={handleDownload}
                  >
                    <Icon name="download" />
                    Exportar (Yelum)
                  </Button>
                </AuthorizationContainer>
              </HeaderActions>
            </HeaderRow>
          </Grid.Column>
        </Grid.Row>

        {/* Alertas - Não mostrar para lotes fechados */}
        {batch.status !== 'CLOSED' && batch.hasSingleItem && (
          <Grid.Row>
            <Grid.Column>
              <AlertBox warning icon>
                <Icon name="warning sign" />
                <Message.Content>
                  <Message.Header>Atenção: Lote com apenas 1 item</Message.Header>
                  <p>
                    Este lote possui apenas <strong>1 item</strong>. A Yelum Seguros exige um
                    mínimo de <strong>2 itens</strong> para transmissão. Se um segundo item não
                    for adicionado até o final do mês, a data de vigência será automaticamente
                    alterada para o mês seguinte.
                  </p>

                </Message.Content>
              </AlertBox>
            </Grid.Column>
          </Grid.Row>
        )}

        {batch.status !== 'CLOSED' && batch.isNearDeadline && (
          <Grid.Row>
            <Grid.Column>
              <AlertBox warning icon>
                <Icon name="clock" />
                <Message.Content>
                  <Message.Header>Urgente: Transmissão Próxima</Message.Header>
                  <p>
                    Faltam <strong>{batch.daysRemaining} dias</strong> para a data de transmissão
                    deste lote ({moment(batch.transmissionDate).format('DD/MM/YYYY')}). Verifique
                    se há pendências.
                  </p>
                </Message.Content>
              </AlertBox>
            </Grid.Column>
          </Grid.Row>
        )}

        {batch.status === 'CLOSED' && (
          <Grid.Row>
            <Grid.Column>
              <AlertBox info icon>
                <Icon name="info circle" />
                <Message.Content>
                  <Message.Header>Lote Finalizado</Message.Header>
                  <p>
                    Este lote foi fechado e não será mais transmitido. O processo foi concluído.
                  </p>
                </Message.Content>
              </AlertBox>
            </Grid.Column>
          </Grid.Row>
        )}

        {/* Informações do Lote */}
        <Grid.Row>
          <Grid.Column width={8}>
            <InfoCard>
              <Card.Content>
                <CardHeaderRow>
                  <Card.Header>Informações do Lote</Card.Header>
                  <AuthorizationContainer
                    action={ActionEnum.save}
                    feature={FeatureEnum.fireinsurances}
                  >
                    <Button basic size="small" onClick={() => setEditModalOpen(true)}>
                      <Icon name="edit" />
                      Editar lote
                    </Button>
                  </AuthorizationContainer>
                </CardHeaderRow>
              </Card.Content>
              <Card.Content>
                <InfoRow>
                  <InfoLabel>Número do Lote:</InfoLabel>
                  <InfoValue>
                    <strong>#{batch.batchNumber}</strong>
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Status:</InfoLabel>
                  <InfoValue>
                    <Label color={getBatchStatusColor(batch.status) as any} size="small">
                      {translateBatchStatus(batch.status)}
                    </Label>
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Código de Cotação:</InfoLabel>
                  <InfoValue>{batch.quotationCode || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Código de Proposta:</InfoLabel>
                  <InfoValue>{batch.proposalCode || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Número da Apólice:</InfoLabel>
                  <InfoValue>{batch.policyNumber || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Criado em:</InfoLabel>
                  <InfoValue>{moment(batch.createdAt).format('DD/MM/YYYY HH:mm')}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Atualizado em:</InfoLabel>
                  <InfoValue>{moment(batch.updatedAt).format('DD/MM/YYYY HH:mm')}</InfoValue>
                </InfoRow>
              </Card.Content>
            </InfoCard>
          </Grid.Column>

          <Grid.Column width={8}>
            <InfoCard>
              <Card.Content>
                <Card.Header>Informações da Imobiliária</Card.Header>
              </Card.Content>
              <Card.Content>
                <InfoRow>
                  <InfoLabel>Nome:</InfoLabel>
                  <InfoValue>
                    <strong>{batch.estate?.name || '-'}</strong>
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Email:</InfoLabel>
                  <InfoValue>{batch.estate?.email || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Seguradora:</InfoLabel>
                  <InfoValue>{batch.insurer?.name || '-'}</InfoValue>
                </InfoRow>
              </Card.Content>
            </InfoCard>
          </Grid.Column>
        </Grid.Row>

        {/* Informações de Transmissão */}
        <Grid.Row>
          <Grid.Column>
            <InfoCard>
              <Card.Content>
                <Card.Header>Informações de Transmissão</Card.Header>
              </Card.Content>
              <Card.Content>
                <Grid>
                  <Grid.Row columns={4}>
                    <Grid.Column textAlign="center">
                      <Segment>
                        <Icon name="calendar" size="big" color="blue" />
                        <Divider />
                        <strong>Data de Transmissão</strong>
                        <div style={{ marginTop: 10 }}>
                          {moment(batch.transmissionDate).format('DD/MM/YYYY')}
                        </div>
                      </Segment>
                    </Grid.Column>
                    <Grid.Column textAlign="center">
                      <Segment>
                        <Icon
                          name="clock"
                          size="big"
                          color={batch.status === 'CLOSED' || batch.status === 'CANCELLED' ? 'grey' : (batch.daysRemaining <= 5 ? 'orange' : 'green')}
                        />
                        <Divider />
                        <strong>Dias Restantes</strong>
                        <div style={{ marginTop: 10 }}>
                          {batch.status === 'CLOSED' || batch.status === 'CANCELLED' ? (
                            <span style={{ color: '#999' }}>-</span>
                          ) : (
                            <Label
                              color={batch.daysRemaining <= 5 ? 'orange' : 'green'}
                              size="large"
                            >
                              {batch.daysRemaining} {batch.daysRemaining === 1 ? 'dia' : 'dias'}
                            </Label>
                          )}
                        </div>
                      </Segment>
                    </Grid.Column>
                    <Grid.Column textAlign="center">
                      <Segment>
                        <Icon name="file alternate" size="big" color="blue" />
                        <Divider />
                        <strong>Itens no Lote</strong>
                        <div style={{ marginTop: 10 }}>
                          <Label size="large" color={batch.hasSingleItem ? 'yellow' : 'green'}>
                            {batch.itemCount}
                          </Label>
                        </div>
                      </Segment>
                    </Grid.Column>
                    <Grid.Column textAlign="center">
                      <Segment>
                        <Icon name="money bill alternate" size="big" color="green" />
                        <Divider />
                        <strong>Dia de Vencimento</strong>
                        <div style={{ marginTop: 10 }}>{batch.dueDate ? `Dia ${batch.dueDate}` : '-'}</div>
                      </Segment>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Card.Content>
            </InfoCard>
          </Grid.Column>
        </Grid.Row>

        {/* Lista de Apólices */}
        <Grid.Row>
          <Grid.Column>
            <Segment>
              <h3>
                <Icon name="list" />
                Apólices no Lote ({activePolicies.length})
              </h3>
              <Table celled striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell collapsing textAlign="center">
                      <AuthorizationContainer
                        action={ActionEnum.save}
                        feature={FeatureEnum.fireinsurances}
                      >
                        <Checkbox
                          checked={allSelected}
                          disabled={selectablePolicyIds.length === 0}
                          onChange={(_, data) =>
                            handleToggleAllPolicies(Boolean(data.checked))
                          }
                        />
                      </AuthorizationContainer>
                    </Table.HeaderCell>
                    <Table.HeaderCell>Número</Table.HeaderCell>
                    <Table.HeaderCell>Imóvel/Endereço</Table.HeaderCell>
                    <Table.HeaderCell>Nome Locatário</Table.HeaderCell>
                    <Table.HeaderCell>Documento Locatário</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Status</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Vigência</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Prêmio</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {activePolicies.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={8} textAlign="center">
                        <Icon name="inbox" size="huge" color="grey" />
                        <p>Nenhuma apólice ativa encontrada neste lote.</p>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    activePolicies.map((policy, index) => {
                      const address = policy.fireInsuranceAnalysis?.fireInsurance?.propertyAddresses;
                      let addressString = '-';
                      if (address) {
                        const parts = [];
                        if (address.street) parts.push(address.street);
                        if (address.number) parts.push(address.number);
                        if (address.complement) parts.push(address.complement);
                        if (address.district) parts.push(address.district);
                        if (address.city && address.state) parts.push(`${address.city}/${address.state}`);
                        if (address.zipcode) parts.push(`CEP: ${address.zipcode}`);
                        addressString = parts.length > 0 ? parts.join(', ') : '-';
                      }

                      const premium = policy.fireInsuranceAnalysis?.grossPremium ||
                        policy.fireInsuranceAnalysis?.commercialPremium ||
                        null;

                      const tenantName = policy.fireInsuranceAnalysis?.fireInsurance?.tenantName || '-';

                      const tenantDocument = policy.fireInsuranceAnalysis?.fireInsurance?.tenantDocument || '-';

                      return (
                        <Table.Row key={policy.id}>
                          <Table.Cell textAlign="center">
                            <AuthorizationContainer
                              action={ActionEnum.save}
                              feature={FeatureEnum.fireinsurances}
                            >
                              <Checkbox
                                checked={selectedPolicyIds.includes(policy.id)}
                                onChange={(_, data) =>
                                  handleTogglePolicy(
                                    policy.id,
                                    Boolean(data.checked),
                                  )
                                }
                              />
                            </AuthorizationContainer>
                          </Table.Cell>
                          <Table.Cell>{index + 1}</Table.Cell>
                          <Table.Cell>{addressString}</Table.Cell>
                          <Table.Cell>{tenantName}</Table.Cell>
                          <Table.Cell>{tenantDocument}</Table.Cell>
                          <Table.Cell textAlign="center">
                            <Label size="small" color="blue">
                              {policy.status}
                            </Label>
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {moment(policy.policyPeriodStart).format('DD/MM/YYYY')} até{' '}
                            {moment(policy.policyPeriodEnd).format('DD/MM/YYYY')}
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {premium
                              ? `R$ ${Number(premium).toFixed(2).replace('.', ',')}`
                              : '-'}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })
                  )}
                </Table.Body>
              </Table>
            </Segment>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <EditBatchCodesModal
        open={editModalOpen}
        batch={batch}
        onClose={() => setEditModalOpen(false)}
        onSuccess={handleEditSuccess}
      />

      <TransmitBatchConfirmModal
        open={transmitModalOpen}
        batch={{
          id: batch.id,
          batchNumber: batch.batchNumber,
          status: batch.status,
          itemCount: activePolicies.length,
          hasSingleItem: activePolicies.length === 1,
          isManualProcessing: batch.isManualProcessing,
          estate: batch.estate,
        }}
        onClose={() => setTransmitModalOpen(false)}
        onSuccess={() => loadBatchDetails()}
      />

      <TransferPoliciesModal
        open={transferModalOpen}
        sourceBatch={{
          id: batch.id,
          batchNumber: batch.batchNumber,
          estate: batch.estate,
        }}
        selectedPolicies={selectedPoliciesForTransfer}
        onClose={() => setTransferModalOpen(false)}
        onSuccess={() => {
          setSelectedPolicyIds([]);
          loadBatchDetails();
        }}
      />

      <SyncBatchNumberingModal
        open={syncModalOpen}
        batch={{
          id: batch.id,
          batchNumber: batch.batchNumber,
          status: batch.status,
          itemCount: activePolicies.length,
          quotationCode: batch.quotationCode,
          insurer: batch.insurer,
          estate: batch.estate,
        }}
        onClose={() => setSyncModalOpen(false)}
        onSuccess={() => loadBatchDetails()}
      />

      <RestartBatchModal
        open={restartModalOpen}
        batch={{
          id: batch.id,
          batchNumber: batch.batchNumber,
          status: batch.status,
          itemCount: activePolicies.length,
          isManualProcessing: batch.isManualProcessing,
          quotationCode: batch.quotationCode,
          proposalCode: batch.proposalCode,
          policyNumber: batch.policyNumber,
          estate: batch.estate,
        }}
        onClose={() => setRestartModalOpen(false)}
        onSuccess={() => loadBatchDetails()}
      />
    </PageContainer>
  );
};

export default FireInsuranceBatchDetails;

